<template>
    <div
        v-if="productPageStore.lipscoreProduct && productPageStore.hasReviews"
        id="product-reviews"
        :data-shortcut-title="t('general.reviews.title')"
    >
        <div class="flex items-center justify-between">
            <h2 class="text-xl font-black md:text-2xl onlywow:md:text-4xl">{{ t('general.reviews.title') }}</h2>

            <!-- Show RealReviews logo -->
            <a href="https://lipscore.com/about-real-reviews/" target="_blank">
                <RealReviewsLogo />
            </a>
        </div>

        <div class="mt-2 grid grid-cols-1 gap-x-8 gap-y-3 lg:mt-4 lg:grid-cols-[minmax(0,30%)_1fr]">
            <div class="mr-3 flex flex-col gap-6 lg:col-start-1 lg:row-start-1">
                <ProductReviewOverview />
            </div>

            <!-- The empty state is desktop only -->
            <div
                class="order-3 mt-2 flex flex-col gap-2 lg:order-none lg:col-start-2 lg:row-start-1 lg:mt-0 lg:gap-3"
                :class="{ 'max-lg:hidden': !hasWrittenReviews }"
                data-test-id="reviews-column"
            >
                <template v-if="hasWrittenReviews">
                    <h3 class="font-bold">{{ t('general.reviews.highest_rated_title') }}</h3>

                    <ProductReviewHighlightSlider class="lg:hidden" :reviews="mobileHighlightedReviews" @on-read-full-review="onReadFullReview" />

                    <div class="hidden lg:grid lg:grid-cols-3 lg:gap-3">
                        <ProductReviewHighlightCard
                            v-for="review in desktopHighlightedReviews"
                            :key="`highlight_review_${review.id}`"
                            :review="review"
                            @on-read-full-review="onReadFullReview"
                        />
                    </div>

                    <div class="flex items-start justify-start lg:hidden">
                        <button
                            type="button"
                            class="transition-200 flex cursor-pointer gap-1 text-sm font-medium text-black underline transition-all outline-none hover:opacity-80"
                            @click="openSidebar()"
                        >
                            {{ t('general.reviews.show_all_reviews', productPageStore.lipscoreProduct.review_count) }}
                            <ArrowRightLongIcon class="size-5 self-center" />
                        </button>
                    </div>
                </template>

                <ProductReviewsEmptyState v-else />
            </div>

            <!-- Switched off per magento group; not mounting it also skips its fetch of the full image set. -->
            <div v-if="!productPageStore.configurableProduct.review_images_hidden" class="order-2 lg:order-none lg:col-span-2 lg:row-start-3">
                <ProductReviewImagesSection :images="productPageStore.reviewImages" />
            </div>

            <div v-if="hasWrittenReviews" class="hidden items-start justify-end lg:col-start-2 lg:row-start-2 lg:flex">
                <button
                    type="button"
                    class="transition-200 flex cursor-pointer gap-1 text-sm font-medium text-black underline transition-all outline-none hover:opacity-80"
                    @click="openSidebar()"
                >
                    {{ t('general.reviews.show_all_reviews', productPageStore.lipscoreProduct.review_count) }}
                    <ArrowRightLongIcon class="size-5 self-center" />
                </button>
            </div>
        </div>

        <ProductReviewsSidebar
            v-model:open="showSidebar"
            :highlight-review-id="sidebarHighlightReviewId"
            :reviews="productPageStore.reviews"
            :review-count="productPageStore.lipscoreProduct.review_count"
            :subtitle="productPageStore.lipscoreProduct.name ?? undefined"
            :images-hidden="productPageStore.configurableProduct.review_images_hidden"
            :page-url="reviewPageUrl"
        />
    </div>
</template>

<script setup lang="ts">
import RealReviewsLogo from '@/icons/reviews/RealReviewsLogo.vue';
import ProductReviewHighlightCard from '@/pages/product-page/partials/product-reviews/partials/ProductReviewHighlightCard.vue';
import ProductReviewHighlightSlider from '@/pages/product-page/partials/product-reviews/partials/ProductReviewHighlightSlider.vue';
import ProductReviewImagesSection from '@/pages/product-page/partials/product-reviews/partials/ProductReviewImagesSection.vue';
import ProductReviewOverview from '@/pages/product-page/partials/product-reviews/partials/ProductReviewOverview.vue';
import ProductReviewsEmptyState from '@/pages/product-page/partials/product-reviews/partials/ProductReviewsEmptyState.vue';
import ProductReviewsSidebar from '@/pages/product-page/partials/product-reviews/partials/ProductReviewsSidebar.vue';
import { useProductPageStore } from '@/stores/pages/ProductPageStore';
import type { LipscoreProductReviewObject } from '@/types/generated';
import ProductReviewsController from '@/types/wayfinder/actions/App/Http/Controllers/Api/Products/ProductReviewsController';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ArrowRightLongIcon from '@/icons/ArrowRightLongIcon.vue';

const { t } = useI18n();
const productPageStore = useProductPageStore();

const desktopHighlightAmount = 3;
const mobileHighlightAmount = 5;
const desktopHighlightedReviews = computed(() => productPageStore.reviews.slice(0, desktopHighlightAmount));
const mobileHighlightedReviews = computed(() => productPageStore.reviews.slice(0, mobileHighlightAmount));
// Star ratings without text get an empty state instead of review cards
const hasWrittenReviews = computed(() => productPageStore.reviews.length > 0);

const showSidebar = ref(false);
const sidebarHighlightReviewId = ref<number | undefined>(undefined);

const openSidebar = (highlightReviewId?: number) => {
    sidebarHighlightReviewId.value = highlightReviewId;
    showSidebar.value = true;
};

const onReadFullReview = (review: LipscoreProductReviewObject) => {
    openSidebar(review.id);
};

// The sidebar only renders with a lipscore product.
const reviewPageUrl = (page: number): string =>
    ProductReviewsController.url({ lipscoreId: productPageStore.lipscoreProduct?.lipscore_id ?? 0, page });
</script>
